import { Modal } from '@mui/material';
import CreateLedgerForm from './CreateLedgerForm';
import { useEffect, useMemo, useState } from 'react';
import AddNewPartyModal from '../../../pages/PrimeFigma/Modals/AddNewPartyModal';
import { usePartyDetailsBYAccountIdQuery } from '../api/accountMaster.api';
import type { groupMasterOptionsType, LedgerModalProp, TselectedGroupType } from '../types';
import { getLedgerTypeFromGroupPath } from '../constants/StaticData';
import { useListGroupMasterQuery } from '../../../redux/apis/accountGroupMaster.api';
import { SimpleLoadingModal } from '../../../components';
import { isBalanceSheetRootGroupId } from '../../../utils/common';

type GroupMasterRow = {
  accountgroupid: number;
  accountgroupname: string;
  path: string;
  accounttypecd: string;
};

const normalizeGroupMasterList = (raw: unknown): GroupMasterRow[] => {
  if (Array.isArray(raw)) {
    return raw as GroupMasterRow[];
  }
  if (raw && typeof raw === 'object' && 'listAccountGroupMaster' in raw) {
    const nested = (raw as { listAccountGroupMaster?: GroupMasterRow[] }).listAccountGroupMaster;
    return Array.isArray(nested) ? nested : [];
  }
  return [];
};

const LedgerModalController = <T,>(props: LedgerModalProp<T>) => {
  const { open = false, groupId = null, accountId = null, title = '', onClose = (cx) => {}, onSave = () => {}, cx = {} as T, updateCurrentLedgerName, restrictedflag = null } = props;
  const { data: rawGroups, isLoading } = useListGroupMasterQuery(undefined);
  const [selectedGroupType, setSelectedGroupType] = useState<TselectedGroupType>({
    groupId: null,
    type: '',
    accounttypecd: '',
  });
  
  const [currentLedgerName, setCurrentLedgerName] = useState<string>('');
  const groupsList = useMemo(() => normalizeGroupMasterList(rawGroups), [rawGroups]);
  
  const isEditMode = !!(accountId && title);

  const groupMasterOptions: groupMasterOptionsType[] = useMemo(
    () =>
      groupsList
        .filter((g) => !props.filterAccountTypeCd || g?.accounttypecd === props.filterAccountTypeCd)
        .filter((g) => {
          if (isEditMode && groupId != null && String(g.accountgroupid) === String(groupId)) {
            return true;
          }
          return !isBalanceSheetRootGroupId(g.accountgroupid);
        })
        .map((g) => ({
          label: g?.accountgroupname,
          value: g?.accountgroupid,
          path: g?.path,
          accounttypecd: g?.accounttypecd,
        })),
    [groupsList, props.filterAccountTypeCd, isEditMode, groupId]
  );

  useEffect(() => {
    const handle = () => {
      if (!groupId) {
        setSelectedGroupType({
          groupId: null,
          type: 'account',
          accounttypecd: '',
        });
        return;
      }
      const sel = groupsList.find((g) => g.accountgroupid === groupId);
      if (!sel) return;
      setSelectedGroupType({
        groupId: groupId,
        type: getLedgerTypeFromGroupPath(sel.path),
        accounttypecd: sel?.accounttypecd || '',
      });
    };
    handle();
  }, [groupsList, groupId]);

  useEffect(() => {
    if (title) {
      setCurrentLedgerName(title);
    }
  }, [title]);

  const { data: partyDetails, isLoading: partyDetailsLoading } = usePartyDetailsBYAccountIdQuery(
    {
      accountsid: accountId || 0,
    },
    {
      skip:
        !accountId ||
        !(selectedGroupType?.type === 'customer' || selectedGroupType?.type === 'supplier'),
    }
  );


  const handleOnSaveParty = ({ accountsId, partyId }: { accountsId: string | number; partyId: string | number }) => {
    let groupid = Number(groupId);
    let partyid = Number(partyId);
    let accountsid = Number(accountsId);
    const isEdit = !!(accountId && title);
    onSave({ cx, accountsId: accountsid, groupId: groupid, partyId: partyid, isEdit });
  };

  const handleUpdateCurrentLedgerName = (ledgerName: string) => {
    setCurrentLedgerName(ledgerName);
    if (updateCurrentLedgerName) {
      updateCurrentLedgerName(ledgerName);
    }
  };

  if (isLoading || partyDetailsLoading) {
    return <SimpleLoadingModal open={true} />;
  }

  if (['customer', 'supplier'].includes(selectedGroupType.type)) {
    const partyId: number | null | undefined =
      accountId && groupId && title && partyDetails && partyDetails?.getPartyMasterAccountWise?.[0]?.accountsid === accountId
        ? partyDetails?.getPartyMasterAccountWise?.[0]?.partyid
        : null;
    
    const newPartyName = currentLedgerName || title || '';
    
 
    return (
      <AddNewPartyModal
        open={true}
        setOpen={() => onClose(cx)}
        partyType={selectedGroupType.type}
        Type="Party"
        partyGroupId={groupId != null ? String(groupId) : ''}
        page="ledgerModal"
        partyParamsID={String(partyId || '')}
        newPartyName={newPartyName}
        accountId={Number(accountId || 0)}
        onSave={handleOnSaveParty}
        partyId=""
        setNewPartyID={() => {}}
        setNewPartyName={() => {}}
        setPartyType={() => {}}
        view={false}
        listPartyDetails={() => {}}
        passFn={() => {}}
      />
    );
  }

  // For non-party groups (bank, account, etc.), show the CreateLedgerForm
  if (selectedGroupType.type === 'bank' || selectedGroupType.type === 'account') {
    return (
      <Modal open={open} aria-labelledby="ledger-modal-title">
        <CreateLedgerForm
          {...props}
          selectedGroupType={selectedGroupType}
          setselectedGroupType={setSelectedGroupType}
          options={groupMasterOptions}
          updateCurrentLedgerName={handleUpdateCurrentLedgerName}
          restrictedflag={restrictedflag}
        />
      </Modal>
    );
  }

  return null;
};

export { LedgerModalController };
